import type { ReactNode } from 'react';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { ChevronDownIcon } from '@utils/icons';
import styles from './ExpandableList.module.css';

interface IExpandableListRowProps {
  name: ReactNode;
  /** The grey line under the name. */
  meta: ReactNode;
  /** The figure on the right. */
  amount: ReactNode;
  /** The small grey line under the figure. */
  amountSub?: ReactNode;
  open: boolean;
  onToggle: () => void;
  /** The action, so a screen reader hears "show history for Rebar" rather
   *  than the row's figures. */
  ariaLabel: string;
  /** What the row unfolds to. */
  children?: ReactNode;
}

/**
 * One row of a logistics list panel — Waste Tracking's and Delivery History's:
 * a name over a grey meta line, a figure on the right, and the whole row the
 * expand control. Shared so the two panels cannot drift apart.
 */
export function ExpandableListRow({ name, meta, amount, amountSub, open, onToggle, ariaLabel, children }: IExpandableListRowProps) {
  return (
    <li className={styles.item}>
      {/* The whole row is the expand affordance, not a 12px chevron. */}
      <button type="button" className={styles.row} aria-expanded={open} aria-label={ariaLabel} onClick={onToggle}>
        <div className={styles.left}>
          <span className={styles.name}>
            {name}
            <span className={styles.caret} data-collapsed={!open} aria-hidden="true">
              <ChevronDownIcon size={12} />
            </span>
          </span>
          <span className={styles.when}>{meta}</span>
        </div>
        <span className={styles.amount}>
          {amount}
          {amountSub ? <small className={styles.amountSub}>{amountSub}</small> : null}
        </span>
      </button>
      {open ? children : null}
    </li>
  );
}

interface IExpandableListProps {
  /** Caps the height where no neighbour panel does. */
  className?: string;
  children: ReactNode;
}

/** The list the rows sit in: fills the panel body and scrolls. */
export function ExpandableList({ className, children }: IExpandableListProps) {
  return (
    <ScrollArea className={className ? `${styles.list} ${className}` : styles.list}>
      <ul className={styles.items}>{children}</ul>
    </ScrollArea>
  );
}

/** What a list panel says when it has nothing to list — centred in the body. */
export function ExpandableListEmpty({ children }: { children: ReactNode }) {
  return <p className={styles.empty}>{children}</p>;
}
